import * as React from "react";
import { Alert, Button } from "antd";
import { useDrawerContext } from "../../../../components/antd/drawerContext";
import { ExternalLink } from "../../../../newcomponents/table/components/ExternalLink";
import { DOCS_URL } from "../../../../envVariables";
import axios from "axios";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { CpDescription } from "../../../gvc/testNewForm/CpDescription";
import { cloneDeep, get } from "lodash";
import { store } from "../../../../store";
import { v4 as uuidv4 } from "uuid";
import { CpArrayString } from "../../../gvc/testNewForm/CpArrayString";
import { CpArrayObject } from "../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemHttpInboundHeaderFilter } from "./CpArrayItemHttpInboundHeaderFilter";
import { HttpInboundHeaderFilterInput } from "../../../../schema/types/workload/input";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { CpLabelTooltip } from "../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../gvc/testNewForm/CpTooltip";
import { CpLabelDirtyIndicator } from "../../../gvc/testNewForm/CpLabelDirtyIndicator";
import { Only } from "../only";

interface Props {}
export const ExternalInbound: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();
  const drawer = useDrawerContext();

  const inboundPublic = useCpFormValue(`firewallConfig.external.inbound.public`);
  const cidrAllowList = useCpFormValue(`firewallConfig.external.inbound.allowCidr`);

  const [myIp, setMyIp] = React.useState<string>(null as any);

  React.useEffect(() => {
    populateIp();
  }, []);

  async function populateIp() {
    let ip = "";
    try {
      const { data: ipRes } = await axios.get("https://ipv4.icanhazip.com/");
      ip = ipRes.trim();
      if (!ip.endsWith(`/32`) && !ip.includes(`/`)) {
        ip += `/32`;
      }
      setMyIp(ip);
    } catch (e) {
      // notification.warning({ message: "Failed", description: "Failed to get your IP address." });
      return;
    }
  }

  async function toggleMyIp(): Promise<void> {
    if (!myIp) {
      return;
    }

    if (inboundPublic) {
      return;
    }

    const form = store.getState().form.instances[id];
    const values = form.values;
    const allowList = get(values, `firewallConfig.external.inbound.allowCidr`);
    let _allowList = cloneDeep(allowList);
    if (_allowList.some((v) => v.value === myIp)) {
      _allowList = _allowList.filter((v) => v.value !== myIp);
    } else {
      _allowList.push({ id: uuidv4(), value: myIp });
    }
    formSetValue(id, `firewallConfig.external.inbound.allowCidr`, _allowList);
    formTriggerValidation(id);
  }

  return (
    <>
      <CpFormItem>
        <CpLabelTooltip>
          <CpLabel name={`firewallConfig.external.inbound.public`}>
            Allow All Inbound Requests
            <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
              <CpLabelDirtyIndicator name={`firewallConfig.external.inbound.public`} />
            </Only>
          </CpLabel>
          <CpSwitch name={`firewallConfig.external.inbound.public`} />
          <CpTooltip tooltipProps={{ title: `Adds 0.0.0.0/0 to CIDR Allow List` }} />
        </CpLabelTooltip>
      </CpFormItem>
      {inboundPublic ? null : (
        <div style={{ maxWidth: 600 }}>
          <CpFormItem>
            <div className="flex items-center gap-4 mb-2">
              <div>CIDR Allow List</div>
              {!myIp || inboundPublic ? null : (
                <Button
                  htmlType={`button`}
                  onClick={() => toggleMyIp()}
                  type={`primary`}
                  className="text-sm"
                  size={`small`}
                >
                  {cidrAllowList.some((v) => v.value === myIp) ? "Remove" : "Add"} My IP
                </Button>
              )}
            </div>
            <CpArrayString
              name={`firewallConfig.external.inbound.allowCidr`}
              noMargin
              dirtyIndicator={{
                isEnabled: true,
                only: {
                  name: "meta.isCreate",
                  checkFn: (v) => v === false,
                },
              }}
            />
          </CpFormItem>
        </div>
      )}
      <div style={{ maxWidth: 600 }}>
        <CpArrayString
          label={`CIDR Block List`}
          name={`firewallConfig.external.inbound.blockedCidr`}
          dirtyIndicator={{
            isEnabled: true,
            only: {
              name: "meta.isCreate",
              checkFn: (v) => v === false,
            },
          }}
        />
      </div>
      <CpFormItem>
        <div className="flex items-baseline gap-2">
          <div>HTTP Inbound Header Filters</div>
          <CpTooltip tooltipProps={{ title: "Does not work on the TCP exposed ports of the workload." }} />
          <span
            className="text-sm link cursor-pointer"
            onClick={() =>
              drawer.setPersistent(
                "workload-firewall-inbound-httpfilters",
                "HTTP Inbound Header Filters",
                <div>
                  <Alert className={`small mb-4`} message={`Does not work on the TCP exposed ports of the workload.`} />
                  <div className="flex flex-col gap-2">
                    <div>
                      Using the inboundHeaderFilter field, you can filter out requests that do not meet certain http
                      header criteria. You can permit permit headers that contain a specific header/value pair or deny
                      them.
                    </div>
                    <div>
                      This can be used in conjunction with geo-headers, to allow/deny requests originating from specific
                      locations.{" "}
                    </div>
                    <div>
                      If multiple allow or deny filters are specified, the resulting operation will be triggered if ANY
                      value is matched. It does not have to match all of them.
                    </div>
                    <ExternalLink
                      url={`${DOCS_URL}/reference/workload/firewall#inbound-http-header-filter`}
                      label="Read more in documentation"
                      disableCopy
                    />
                  </div>
                </div>,
              )
            }
          >
            Details
          </span>
        </div>
      </CpFormItem>
      <div style={{ maxWidth: 1200 }}>
        <CpArrayObject
          labels={[
            { label: "Key", span: 2 },
            { label: "Type", span: 2 },
            { label: "Values", span: 8 },
          ]}
          factoryFn={() => {
            const newFilter: HttpInboundHeaderFilterInput = {
              id: uuidv4(),
              key: "",
              type: "allowed",
              values: [],
            };
            return newFilter;
          }}
          name={`firewallConfig.external.httpInboundHeaderFilter`}
          dirtyIndicator={{
            isEnabled: true,
            only: {
              name: "meta.isCreate",
              checkFn: (v) => v === false,
            },
          }}
        >
          <CpArrayItemHttpInboundHeaderFilter />
        </CpArrayObject>
      </div>
    </>
  );
};
